import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { ThemeProvider } from '@mui/material/styles';
import themes from '../../layouts/theme-main/Theme';
import SendIcon from '@mui/icons-material/Send';
import { Card, FormControl, Grid, IconButton, InputAdornment, InputLabel, FormHelperText, OutlinedInput, useMediaQuery } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSearchParams } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { closeLoadingApi, openLoadingApi } from '../../redux/create-actions/LoadingAction';
import { useDispatch, useSelector } from 'react-redux';
import { showAlert } from '../../redux/create-actions/AlertAction';
import { graphUserPassReset } from '../../services/Authenticate';

const validationSchema = Yup.object().shape({
  newPassword: Yup.string()
    .required("This field is required")
    .min(8, 'Password is too short - should be 8 chars minimum.'),
  confirmPassword: Yup.string()
    .required("This field is required")
    .when("newPassword", {
      is: val => (val && val.length > 0 ? true : false),
      then: Yup.string().oneOf(
        [Yup.ref("newPassword")],
        "Both password need to be the same"
      )
    })
});

export default function PasswordReset() {
  const customization = useSelector(state => state.customization);
  const theme = themes(customization);
  const matchDownSM = useMediaQuery(theme.breakpoints.down('sm'));
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [searchParams,] = useSearchParams({});
  const customerId = searchParams.get('customerId');
  const code = searchParams.get('code');
  const [isSuccess, setIsSuccess] = useState(false);
  const dispatch = useDispatch();

  const formik = useFormik({
    initialValues: {
      newPassword: '',
      confirmPassword: '',
      customerId,
      code,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => { handleSubmit(values) }
  });

  const handleSubmit = (e) => {

    let alert = {
      anchorOrigin: {
        vertical: 'top',
        horizontal: 'center'
      },
      isOpen: true,
      severity: 'error',
      title: 'Error',
      message: '',
    };
    dispatch(openLoadingApi());

    graphUserPassReset(code, e.newPassword, customerId)
      .then(result => {
        setIsSuccess(true)
        dispatch(closeLoadingApi());
      })
      .catch(error => {
        const err = JSON.parse(JSON.stringify(error));
        alert = { ...alert, message: err.response.errors[0].message };
        dispatch(showAlert(alert));
        dispatch(closeLoadingApi());
      })
  }

  return (
    <ThemeProvider theme={theme}>
      <Container component="main" maxWidth="sm"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh'
        }}>
        <Card
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            maxWidth: '465px',
            minWidth: '200px',
            p: 2,
            backgroundColor: '#fff',
            boxShadow: '0 6px 16px 0 rgb(0 0 0 / 20%)',
          }}
        >
          {isSuccess === false ? <Typography
            variant={matchDownSM ? 'h3' : 'h2'}
            sx={{
              color: 'secondary.main',
              pt: 2,
              pb: 2
            }} gutterBottom>
            Enter New Password
          </Typography> :
            <div>
              <CheckCircleOutlineIcon sx={{ fontSize: '50px', color: 'secondary.main' }} />
              <Typography
                variant={matchDownSM ? 'h3' : 'h2'}
                sx={{
                  color: 'secondary.main',
                  pt: 2,
                  pb: 2
                }} gutterBottom>
                Password Reset Successful
              </Typography>
            </div>
          }
          {isSuccess === 'false' ? <Typography variant="body1" sx={{ color: "#000", maxWidth: '358px', position: 'relative', left: '50%', transform: 'translateX(-50%)' }}>
            Looks like you are trying to reset the password for the account. Please enter your new password twice. So we can verify you typed it correctly.
          </Typography> : <Typography variant="body1" sx={{ color: "#000", maxWidth: '358px', position: 'relative', left: '50%', transform: 'translateX(-50%)' }}>
            Awesome. You have successfully reset the password for your account.
          </Typography>
          }
          <Box
            paddingTop="30px"
            paddingBottom=" 10px"
            paddingRight={matchDownSM ? '16px' : '35px'}
            paddingLeft={matchDownSM ? '16px' : '35px'}
            sx={{
              borderRadius: '5px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <form onSubmit={formik.handleSubmit} noValidate>
              {isSuccess === false ?
                <div>
                  <Grid sx={{ mb: 2 }} >
                    <FormControl fullWidth
                      error={formik.touched.newPassword && Boolean(formik.errors.newPassword)}
                    >
                      <InputLabel htmlFor=
                        "password"> New Password</InputLabel>
                      <OutlinedInput
                        id="newPassword"
                        type={showPassword ? 'text' : 'password'}
                        fullWidth
                        required
                        label="New Password"
                        size={matchDownSM ? 'small' : 'large'}
                        value={formik.values.newPassword}
                        onChange={formik.handleChange}
                        endAdornment={
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={e => { setShowPassword(!showPassword) }}
                              onMouseDown={(e) => e.preventDefault()}
                              edge="end"
                              size="small"
                            >
                              {showPassword ? <Visibility color="secondary" /> : <VisibilityOff color="secondary" />}
                            </IconButton>
                          </InputAdornment>
                        }
                        inputProps={{}} />
                      {formik.touched.newPassword && formik.errors.newPassword && (
                        <FormHelperText error id="standard-weight-helper-text-email-login">
                          {formik.errors.newPassword}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                  <Grid sx={{ mb: 4 }}>
                    <FormControl fullWidth
                      error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                    >
                      <InputLabel htmlFor=
                        "confirmPassword">Confirm Password</InputLabel>
                      <OutlinedInput
                        id="confirmPassword"
                        type={showConfirmPassword ? 'text' : 'password'}
                        fullWidth
                        variant="contained"
                        size={matchDownSM ? 'small' : 'large'}
                        required
                        value={formik.values.confirmPassword}
                        onChange={formik.handleChange}
                        endAdornment={
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={e => { setShowConfirmPassword(!showConfirmPassword) }}
                              onMouseDown={(e) => e.preventDefault()}
                              edge="end"
                              size="small"
                            >
                              {showConfirmPassword ? <Visibility color="secondary" /> : <VisibilityOff color="secondary" />}
                            </IconButton>
                          </InputAdornment>
                        }
                        label="Confirm Password"
                        inputProps={{}}
                      />
                      {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                        <FormHelperText error id="standard-weight-helper-text-email-login">
                          {formik.errors.confirmPassword}
                        </FormHelperText>
                      )}
                    </FormControl>
                  </Grid>

                </div>
                :
                <></>
              }
              {isSuccess === false ?
                <Button
                  type="submit"
                  variant="contained"
                  endIcon={<SendIcon />}
                  size={matchDownSM ? 'small' : 'medium'}
                  sx={{
                    mt: 3,
                    mb: 3,
                    bgcolor: 'secondary.main',
                    borderRadius: '4px',
                  }}
                >
                  Change Password
                </Button>
                :
                <Button
                  variant="contained"
                  href="/signin"
                  size='large'
                  sx={{
                    mt: 3, mb: 3,
                    bgcolor: 'secondary.main',
                    borderRadius: '4px',
                    width: '250px'
                  }}
                >
                  Login
                </Button>}
            </form>
          </Box>
        </Card>
      </Container>
    </ThemeProvider>
  );
}


